/**
 * Pieces both lesson players share (LessonViewer for screenshot lessons, VideoLessonViewer for video lessons): the
 * sticky header with the way back, the step's title and transcript, the player keys and the continue phase.
 */
import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/Icon';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { nextPhase, type ContinueEvent, type ContinuePhase } from './continueModel';
import type { ProgressMap } from './progressModel';
import { playerKeyOf, type PlayerKey, type TutorialLesson, type TutorialStep } from './tutorialsModel';

export interface ViewerProps {
  lesson: TutorialLesson;
  nextLesson: TutorialLesson | null;
  progress: ProgressMap;
  /** Start playing on open: the viewer opened it (a card, Up next, Next lesson) or the countdown did after a click. */
  autoPlay: boolean;
  onClose: () => void;
  onOpenLesson: (id: string) => void;
}

/** ←/→/space/Esc anywhere on the page, except while typing or on a focused control (see playerKeyOf). */
export function usePlayerKeys(handler: (key: PlayerKey) => void) {
  const latest = useRef(handler);
  latest.current = handler;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target instanceof HTMLElement ? e.target : null;
      const key = playerKeyOf({ key: e.key, tag: target?.tagName.toLowerCase(), editable: target?.isContentEditable, modifier: e.metaKey || e.ctrlKey || e.altKey });
      if (!key || e.defaultPrevented) return;
      e.preventDefault();
      latest.current(key);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

/** Sticky under the top bar, so the way back is visible at every scroll position and width. */
export function ViewerHeader({ lesson, onClose }: { lesson: TutorialLesson; onClose: () => void }) {
  return (
    <div className="sticky top-16 z-20 -mx-6 -mt-6 flex items-center gap-2 border-b bg-background/90 px-6 py-2 backdrop-blur max-sm:top-14 max-sm:-mx-4 max-sm:-mt-4 max-sm:px-4">
      <Button variant="ghost" onClick={onClose} aria-label="Back to all tutorials" data-testid="lesson-close" className="-ml-2 shrink-0 max-sm:w-11 max-sm:px-0">
        <Icon name="arrow_back" size={20} />
        <span className="max-sm:hidden">All tutorials</span>
      </Button>
      <div className="min-w-0 flex-1">
        <h1 data-testid="lesson-title" className="truncate text-[15px] font-semibold leading-tight text-foreground">
          {lesson.title}
        </h1>
        <p className="truncate text-xs text-muted-foreground">{lesson.audience === 'operator' ? 'Hangar administration' : lesson.group}</p>
      </div>
      <kbd className="hidden shrink-0 rounded border bg-secondary px-1.5 py-0.5 text-[11px] text-muted-foreground lg:inline">Esc</kbd>
    </div>
  );
}

/** The current step's title and its full narration (the transcript of what the captions show line by line). */
export function StepNarration({ step, index }: { step: TutorialStep; index: number }) {
  return (
    <div data-testid="lesson-narration" className="flex flex-col gap-1.5 px-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="tabular text-xs font-semibold uppercase tracking-[0.08em] text-subtle">Step {index + 1}</span>
        {step.kind && !['page', 'overview'].includes(step.kind) ? <Badge variant="outline" className="capitalize">{step.kind}</Badge> : null}
      </div>
      <h2 className="text-lg font-semibold tracking-tight text-foreground">{step.title}</h2>
      {step.narration ? <p className="max-w-[75ch] text-sm leading-relaxed text-muted-foreground">{step.narration}</p> : null}
    </div>
  );
}

/** The "continue to the next lesson" phase of this lesson (continueModel.ts), fed only by the viewer's own actions. */
export function useContinuePhase(hasNext: boolean) {
  const [phase, setPhase] = useState<ContinuePhase>('armed');
  const send = (event: ContinueEvent) => setPhase((p) => nextPhase(p, event, hasNext));
  return { phase, send };
}
